import { MotionImage, MotionText } from '@shared/atoms';
import React, { useCallback, useState } from 'react';

import chatIcon from '@assets/images/earlyWarning/chat-icon.svg';
import thoughtIcon from '@assets/images/icons/thought-icon.svg';

import { formatDateStringSplitDots } from '@utils/date.util';
import { InstantMessagingModal } from '../InstantMessagingModal/InstantMessagingModal';
import { SystemButton } from '../SystemButton/SystemButton';
import { ViewThoughtCommentModal } from '../ViewThoughtCommentModal/ViewThoughtCommentModal';
import styles from './index.module.scss';

interface ShareThoughtAndSpeakUpCardProps {
  id: number;
  mainTitle: string;
  commentDate: string;
  comment: string;
  responseDate: string | null;
  response: string;
  buttonText?: string;
  questionnaireName?: string;
  type?: 'thoughts' | 'speakUps';
  questionLabel?: string;
  score?: number;
}

export const ShareThoughtAndSpeakUpCard: React.FC<ShareThoughtAndSpeakUpCardProps> = ({
  id,
  mainTitle,
  commentDate,
  comment,
  responseDate,
  response,
  buttonText = 'View & Message',
  questionnaireName,
  type = 'speakUps',
  questionLabel,
  score,
}) => {
  const [openViewCommentsModal, setOpenViewCommentsModal] = useState(false);

  const handleOpenViewCommentModal = useCallback(() => {
    setOpenViewCommentsModal(true);
  }, []);

  const handleCloseViewCommentModal = useCallback(() => {
    setOpenViewCommentsModal(false);
  }, []);

  return (
    <div className={styles.cardContainer}>
      <div className={`${styles.header}`}>
        <div
          className={
            type === 'thoughts' ? styles.thoughtIconContainer : styles.speakUpIconContainer
          }
        >
          <MotionImage
            src={type === 'speakUps' ? chatIcon : thoughtIcon}
            alt="Chat Icon"
            className={styles.chatIcon}
          />
          <MotionText as="h3" className={styles.title} text={mainTitle} />
        </div>

        {type === 'speakUps' && (
          <div className={styles.rightSide}>
            {questionnaireName && (
              <div className={styles.questionnaireNameTag}>
                <MotionText as="span" className={styles.span} text={questionnaireName} />
              </div>
            )}
          </div>
        )}

        {type === 'thoughts' && (
          <div className={styles.rightSide}>
            {questionnaireName && (
              <div className={styles.questionnaireNameTag}>
                <MotionText as="span" className={styles.span} text={questionnaireName} />
              </div>
            )}
            {questionLabel && score && (
              <div className={styles.cardQuestName}>
                <p>
                  {questionLabel} &nbsp; <span>{score}</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <MotionText
        as="h4"
        className={type === 'thoughts' ? styles.commentDateThought : styles.commentDateSpeakUp}
        text={`Your comment on ${formatDateStringSplitDots(commentDate)}`}
      />
      <blockquote className={styles.commentText}>‘{comment}’</blockquote>

      <div className={styles.responseContainer}>
        <MotionText
          as="h4"
          className={styles.responseDate}
          text={
            responseDate
              ? `Response on ${formatDateStringSplitDots(responseDate)}`
              : 'Awaiting Response'
          }
        />
        <MotionText as="p" className={styles.responseText} text={` ${response}`} />
      </div>

      <div className={styles.footer}>
        <SystemButton
          variant="primaryDarkSkyBlue"
          onClick={handleOpenViewCommentModal}
          disabled={type === 'thoughts' && !responseDate}
        >
          {buttonText}
        </SystemButton>
      </div>

      {type === 'speakUps' ? (
        <InstantMessagingModal
          title={questionnaireName}
          isOpen={openViewCommentsModal}
          onClose={handleCloseViewCommentModal}
          speakUpId={id}
          isUserInterface
        />
      ) : (
        <ViewThoughtCommentModal
          title={questionnaireName}
          isOpen={openViewCommentsModal}
          onClose={handleCloseViewCommentModal}
        >
          {response && response.split('\n').join('<br/>')}
        </ViewThoughtCommentModal>
      )}
    </div>
  );
};

ShareThoughtAndSpeakUpCard.defaultProps = {
  buttonText: 'View & Message',
  questionnaireName: undefined,
  type: 'speakUps',
  questionLabel: undefined,
  score: undefined,
};
